import { Center, Stack, Tooltip, UnstyledButton } from "@mantine/core";
import { forwardRef, useMemo } from "react";
import {
    type ChatTask,
    selectChatAutopilot,
    selectChatNeglection,
    selectUnreadCount,
    useActiveChatMessages,
    useChatStore,
} from "@/sdk/agent/chat";
import { DEFAULT_CHAT_WRAPPING_DURATION } from "@/sdk/agent/config";
import { getSessionByTaskId } from "@/sdk/sessions/getters";
import classes from "../chat-components.module.css";
import { ChatAvatar } from "./ChatAvatar";
import { ChatPreviewBuilder } from "./ChatPreviewBuilder";
import { NotificationTimerRing } from "./NotificationTimerRing";
import { QueuedChatIndicator } from "./QueuedChatIndicator";
import { SelectedChatIndicator } from "./SelectedChatIndicator";
import { getChatVariant, getColorByVariant } from "./variants";

export interface QueuedChatProps {
    chat: ChatTask;
    onChatSelected: (chat: ChatTask) => void;
    isChatSelected: boolean;
}

interface TimerProps {
    initialPercentage: number | undefined;
    duration: number | undefined;
}

const isValidTimestamp = (val: unknown): val is number => typeof val === "number" && val > 0;

/** Calculates timer progress from start/end timestamps (used for neglection, customer absent, etc.) */
function getTimerFromTimestamps(startMs: number, endMs: number): TimerProps {
    const now = Date.now();
    const totalMs = endMs - startMs;
    if (totalMs <= 0) return { initialPercentage: 100, duration: 0 };
    const elapsedMs = now - startMs;
    return {
        initialPercentage: Math.min(Math.floor((100 * elapsedMs) / totalMs), 100),
        duration: Math.max(endMs - now, 0),
    };
}

/** Calculates timer progress from expiration timestamp (used for wrapping fallback) */
function getTimerFromExpiration(expirationMs: number, totalDurationMs: number): TimerProps {
    const now = Date.now();
    if (totalDurationMs <= 0) return { initialPercentage: 100, duration: 0 };
    const remainingMs = Math.max(expirationMs - now, 0);
    const elapsedMs = totalDurationMs - remainingMs;
    return {
        initialPercentage: Math.min(Math.floor((100 * elapsedMs) / totalDurationMs), 100),
        duration: remainingMs,
    };
}

export const QueuedChat = forwardRef<HTMLButtonElement, QueuedChatProps>(
    ({ onChatSelected, chat, isChatSelected, ...rest }, ref) => {
        const messages = useActiveChatMessages(chat);
        const lastMessageDate = messages.at(-1)?.dateCreated ?? null;
        const unreadMessageCount = useChatStore(selectUnreadCount(chat.chatId));

        // Subscribe to neglection and autopilot state to trigger re-renders for concurrent chats
        const neglectionState = useChatStore(selectChatNeglection(chat.chatId));
        const autopilotState = useChatStore(selectChatAutopilot(chat.chatId));

        // Recalculate variant when neglection or autopilot state changes
        // oxlint-disable-next-line exhaustive-deps -- intentional: these trigger recalculation even though getChatVariant reads from the task
        const variant = useMemo(() => getChatVariant(chat), [chat, neglectionState, autopilotState]);
        const variantColor = useMemo(() => getColorByVariant(variant), [variant]);

        /** Memoized session ID that only changes when the task ID changes */
        const sessionId = useMemo(() => {
            const session = getSessionByTaskId(chat.id);
            if (!session) {
                throw new Error(`Session not found for task ID: ${chat.id} in chat queue`); // should be an impossible case
            }
            return session.id;
        }, [chat.id]);

        /** Returns timer ring progress/duration based on chat state */
        const getTimerProps = (): TimerProps => {
            const { changeStateAt, stateLastChangedAt } = chat;

            if (isValidTimestamp(changeStateAt) && isValidTimestamp(stateLastChangedAt)) {
                return getTimerFromTimestamps(stateLastChangedAt, changeStateAt);
            }

            // Fallback for wrapping tasks when timestamps aren't available
            if (chat.status === "wrapping") {
                const expiration = getSessionByTaskId(chat.id)?.wrappingState?.expirationTimestamp;
                if (expiration) {
                    return getTimerFromExpiration(expiration, chat.wrappingDuration || DEFAULT_CHAT_WRAPPING_DURATION);
                }
            }

            return { initialPercentage: undefined, duration: undefined };
        };

        const isPending = chat.status === "pending";

        return (
            <Tooltip
                label={isPending ? "This chat cannot be selected until the customer is connected" : undefined}
                position="right"
                withArrow
                disabled={!isPending}
                transitionProps={{ duration: 300 }}
            >
                <UnstyledButton
                    ref={ref}
                    id={`chat-select-chatId:${chat.chatId || "ERROR_UNDEFINED_CHAT_ID"}`}
                    {...rest}
                    className={classes.queued_chat_interactive_area}
                    onClick={() => onChatSelected(chat)}
                    disabled={isPending}
                    data-selected={isChatSelected}
                    data-pending={isPending}
                >
                    <SelectedChatIndicator isChatSelected={isChatSelected} />
                    <NotificationTimerRing
                        variantColor={variantColor}
                        withRippleAnimation={variant === "neglected"} //TODO: support pre-neglect-warning phase in [EWPM-175]
                        ring={{ size: 73, thickness: 3.5, scaleRatio: 1 }}
                        timer={getTimerProps()}
                    >
                        <Center>
                            <QueuedChatIndicator
                                color="asurion.5"
                                size={20}
                                label={unreadMessageCount}
                                visible={variant !== "incoming" && !!unreadMessageCount}
                            >
                                <ChatAvatar
                                    sessionId={sessionId}
                                    customerName={chat.customerName}
                                    variant={variant}
                                    variantColor={variantColor}
                                    avatarSize={48}
                                />
                            </QueuedChatIndicator>
                        </Center>
                    </NotificationTimerRing>
                    <ChatPreviewBuilder
                        chat={chat}
                        lastMessageDate={lastMessageDate}
                        variant={variant}
                        variantColor={variantColor}
                        wrapper={Stack}
                    />
                </UnstyledButton>
            </Tooltip>
        );
    },
);

QueuedChat.displayName = "QueuedChat";
